import React, { useCallback } from "react";
import { DocumentRequirement } from "../types/config";

interface DocumentUploadProps {
  requirement: DocumentRequirement;
  onFileSelect: (docId: string, file: File) => void;
  selectedFile?: File;
  error?: string;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  requirement,
  onFileSelect,
  selectedFile,
  error,
}) => {
  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        // Check file size
        if (file.size > requirement.maxSizeMB * 1024 * 1024) {
          alert(`File size must be less than ${requirement.maxSizeMB}MB`);
          return;
        }

        // Check file type
        const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
        if (!requirement.acceptedFormats.includes(fileExtension)) {
          alert(
            `File type must be one of: ${requirement.acceptedFormats.join(
              ", "
            )}`
          );
          return;
        }

        onFileSelect(requirement.id, file);
      }
    },
    [requirement, onFileSelect]
  );

  return (
    <div className="p-4 border rounded-lg bg-gray-50">
      <div className="mb-2">
        <h3 className="font-medium text-gray-900">
          {requirement.name}
          {requirement.required && <span className="text-red-500 ml-1">*</span>}
        </h3>
        <p className="text-sm text-gray-500">{requirement.description}</p>
      </div>

      <div className="mt-2">
        <input
          type="file"
          onChange={handleFileChange}
          accept={requirement.acceptedFormats.join(",")}
          className="hidden"
          id={`file-${requirement.id}`}
        />
        <label
          htmlFor={`file-${requirement.id}`}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
        >
          {selectedFile ? "Change File" : "Select File"}
        </label>
        {selectedFile && (
          <span className="ml-3 text-sm text-gray-600">
            {selectedFile.name}
          </span>
        )}
      </div>

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

      <div className="mt-2 text-xs text-gray-500">
        Accepted formats: {requirement.acceptedFormats.join(", ")}
        <br />
        Maximum size: {requirement.maxSizeMB}MB
      </div>
    </div>
  );
};

export default DocumentUpload;
